import { AlertTriangle, Shield, Clock, XCircle, LucideIcon, ArrowLeft } from 'lucide-react'

export type AccessDeniedStatus = 'inactive' | 'suspended' | 'pending_verification' | 'no_access'

export interface AccessDeniedConfig {
  icon: LucideIcon
  iconColor: string
  bgColor: string
  borderColor: string
  title: string
  description: string
  actionText: string
  showRetry: boolean
}

export interface AccessDeniedUser {
  email?: string
  id?: string
  name?: string
  tenantName?: string
}

interface AccessDeniedPageProps {
  status?: AccessDeniedStatus
  message?: string
  user?: AccessDeniedUser
  onContactAdmin: () => void
  onRetry?: () => void
  onLogout: () => void
  supportEmail?: string
  getStatusConfig?: (status: AccessDeniedStatus, message?: string) => AccessDeniedConfig
  logoutText?: string
}

export function getDefaultStatusConfig(status: AccessDeniedStatus, message?: string): AccessDeniedConfig {
  switch (status) {
    case 'inactive':
      return {
        icon: XCircle,
        iconColor: 'text-orange-500',
        bgColor: 'bg-orange-50',
        borderColor: 'border-orange-200',
        title: 'Access Inactive',
        description: message || 'Your access is currently inactive. This could be due to account maintenance or administrative action.',
        actionText: 'Contact Administrator',
        showRetry: false
      }
    
    case 'suspended':
      return {
        icon: Shield,
        iconColor: 'text-red-500',
        bgColor: 'bg-red-50',
        borderColor: 'border-red-200',
        title: 'Access Suspended',
        description: message || 'Your access has been temporarily suspended. Please contact your administrator for assistance.',
        actionText: 'Contact Administrator',
        showRetry: false
      }
    
    case 'pending_verification':
      return {
        icon: Clock,
        iconColor: 'text-yellow-500',
        bgColor: 'bg-yellow-50',
        borderColor: 'border-yellow-200',
        title: 'Access Pending Verification',
        description: message || 'Your access is currently being verified. This process usually takes 24-48 hours.',
        actionText: 'Contact Support',
        showRetry: true
      }
    
    default:
      return {
        icon: AlertTriangle,
        iconColor: 'text-gray-500',
        bgColor: 'bg-gray-50',
        borderColor: 'border-gray-200',
        title: 'Access Required',
        description: message || 'You do not have access. Please contact your administrator to request access.',
        actionText: 'Request Access',
        showRetry: false
      }
  }
}

export function AccessDeniedPage({ 
  status = 'no_access', 
  message,
  user,
  onContactAdmin,
  onRetry,
  onLogout,
  supportEmail = 'support@airclou.com',
  getStatusConfig = getDefaultStatusConfig,
  logoutText = 'Sign Out'
}: AccessDeniedPageProps) {
  const config = getStatusConfig(status, message)
  const IconComponent = config.icon

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <div className="bg-white py-8 px-4 shadow sm:rounded-lg sm:px-10">
          <div className="text-center">
            {/* Icon */}
            <div className={`mx-auto flex items-center justify-center h-16 w-16 rounded-full ${config.bgColor} ${config.borderColor} border`}>
              <IconComponent className={`h-8 w-8 ${config.iconColor}`} />
            </div>
            
            {/* Title */}
            <h1 className="mt-6 text-2xl font-bold text-gray-900">
              {config.title}
            </h1>
            
            {/* Description */}
            <p className="mt-4 text-sm text-gray-600 leading-relaxed">
              {config.description}
            </p>

            {/* User Information */}
            {user && (
              <div className="mt-6 p-4 bg-gray-50 rounded-md">
                <p className="text-xs text-gray-500 mb-1">Signed in as:</p>
                <p className="text-sm font-medium text-gray-900">{user.email}</p>
                {user.tenantName && user.tenantName !== 'Unknown Organization' && (
                  <p className="text-xs text-gray-500 mt-1">Organization: {user.tenantName}</p>
                )}
              </div>
            )}

            {/* Actions */}
            <div className="mt-8 space-y-3">
              {config.showRetry && onRetry && (
                <button
                  onClick={onRetry}
                  className="w-full flex justify-center items-center px-4 py-2 bg-primary text-primary-foreground rounded-md hover:bg-[hsl(var(--primary-hover))] focus:outline-none focus:ring-2 focus:ring-primary"
                >
                  <Clock className="h-4 w-4 mr-2" />
                  Check Status Again
                </button>
              )}
              
              <button
                onClick={onContactAdmin}
                className={`w-full flex justify-center items-center px-4 py-2 text-sm rounded-md focus:outline-none focus:ring-2 focus:ring-primary ${
                  config.showRetry 
                    ? 'border border-gray-300 text-gray-700 hover:bg-gray-50' 
                    : 'bg-primary text-primary-foreground hover:bg-[hsl(var(--primary-hover))]'
                }`}
              >
                {config.actionText}
              </button>

              <button
                onClick={onLogout}
                className="w-full flex justify-center items-center px-4 py-2 text-sm text-gray-500 hover:text-gray-700 focus:outline-none focus:ring-2 focus:ring-primary"
              >
                <ArrowLeft className="h-4 w-4 mr-2" />
                {logoutText}
              </button>
            </div>

            {/* Additional Help Text */}
            <div className="mt-8 pt-6 border-t border-gray-200">
              <p className="text-xs text-gray-500">
                Need immediate assistance? Contact{' '}
                <a 
                  href={`mailto:${supportEmail}`}
                  className="text-primary hover:text-[hsl(var(--primary-hover))]"
                >
                  {supportEmail}
                </a>
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
